import { useCallback, useEffect, useState } from 'react';

import { Box } from '@mui/material';
import EnterpriseCodeEnum from '@settings/models/enterprises-codes.enum';
import JNumberField from '@atoms/JNumberField';
import JTextFieldBase from '@atoms/JTextFieldBase';
import Jpaper from '@atoms/JPaper';
import { Loading } from '@atoms/Loading';
import NumberSetting from '../atoms/NumberSetting';
import UpdateSettingDto from '@settings/models/dto/update-setting.dto';
import { useGetEnterpriseSettings } from '@settings/queries/useGetSettings';
import useJConfirmationBar from '@/hooks/useJConfirmationBar';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUpdateSettings } from '@settings/mutations/settingsMutations';

const EnterprisesSettings = () => {
  const { data, isFetching: isSettingsFetching } = useGetEnterpriseSettings();
  const {
    mutate: mutateSettings,
    isLoading: isMutateSettingsLoading,
    isSuccess: isMutateSettingsSuccess,
  } = useUpdateSettings();
  const { show, close } = useJConfirmationBar();
  const queryClient = useQueryClient();
  const [payload, setPayload] = useState<UpdateSettingDto[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    EnterpriseCodeEnum[]
  >([]);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [isDirty, setIsDirty] = useState(false);
  const { t } = useTranslation();
  const settings = data?.data;

  const addValue = (code: EnterpriseCodeEnum, value: string) => {
    setPayload((prev) => [...prev, { code, value }]);
  };
  const updateValue = (
    code: EnterpriseCodeEnum,
    value: string,
    index: number,
  ) => {
    const updatedPayload = [...payload];
    updatedPayload[index] = { code, value };
    setPayload(updatedPayload);
  };

  const onChangeSetting = (code: EnterpriseCodeEnum, value: string) => {
    // set is dirty
    if (!isDirty) {
      setIsDirty(true);
    }
    // Check if the code already exists in payload
    const existingIndex = payload.findIndex((item) => item.code === code);
    if (existingIndex !== -1) {
      updateValue(code, value, existingIndex);
    } else {
      addValue(code, value);
    }
  };

  const getDefaultValueByCode = (
    code: EnterpriseCodeEnum,
  ): string | undefined => {
    return settings?.find((item) => item.code === code)?.defaultValue;
  };
  const getValueByCode = (code: EnterpriseCodeEnum): number => {
    const defaultvalue = settings?.find((item) => item.code === code)?.value;
    const payloadValue = payload?.find((item) => item.code === code)?.value;
    return Number(payloadValue ?? defaultvalue);
  };

  const isFieldHidden = useCallback(
    (label: string, rank: number): boolean => {
      if (rank.toLocaleString() === searchText) return false;
      return Boolean(
        searchText &&
          !label.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase()),
      );
    },
    [searchText],
  );

  const isFieldNonValid = ({
    code,
    minValue,
    maxValue,
  }: {
    code: EnterpriseCodeEnum;
    minValue?: number;
    maxValue?: number;
  }): boolean => {
    const valueIncluded = validationErrors.includes(code);
    const val = getValueByCode(code);
    if (maxValue && val > maxValue) {
      if (!valueIncluded) setValidationErrors((prev) => [...prev, code]);
      return true;
    }
    if (minValue && val < minValue) {
      if (!valueIncluded) setValidationErrors((prev) => [...prev, code]);
      return true;
    }
    if (valueIncluded) {
      const updatedItems = validationErrors.filter((item) => code !== item);
      setValidationErrors(updatedItems);
    }
    return false;
  };

  const entListCandSearchAreaRangeMinValidation = isFieldNonValid({
    code: EnterpriseCodeEnum.entListCandSearchAreaRangeMin,
    maxValue: getValueByCode(EnterpriseCodeEnum.entListCandSearchAreaRangeMax),
  });
  const entListCandSearchAreaRangeMaxValidation = isFieldNonValid({
    code: EnterpriseCodeEnum.entListCandSearchAreaRangeMax,
    minValue: getValueByCode(EnterpriseCodeEnum.entListCandSearchAreaRangeMin),
  });
  const entListCandExpMinValidation = isFieldNonValid({
    code: EnterpriseCodeEnum.entListCandExpMin,
    maxValue: getValueByCode(EnterpriseCodeEnum.entListCandExpMax),
  });
  const entListCandExpMaxValidation = isFieldNonValid({
    code: EnterpriseCodeEnum.entListCandExpMax,
    minValue: getValueByCode(EnterpriseCodeEnum.entListCandExpMin),
  });

  useEffect(() => {
    if (!isDirty) return;
    show({
      onClose: () => {
        queryClient.invalidateQueries(['settings-enterprise']);
      },
      onConfirm: () => mutateSettings({ payload }),
    });
  }, [isDirty, payload]);

  useEffect(() => {
    return () => close();
  }, []);

  useEffect(() => {
    if (!isMutateSettingsSuccess) return;
    close();
  }, [isMutateSettingsSuccess]);

  useEffect(() => {
    if (validationErrors.length === 0) return;
    close();
  }, [validationErrors.length]);

  const isNameEnterpriseHidden = isFieldHidden(t('NAME_ENTERPRISE'), 1);
  const isSirenNumberHidden = isFieldHidden(t('SIREN_NUMBER'), 2);
  const isEntEstVideoHidden = isFieldHidden(t('ENT_EST_VIDEO'), 3);
  const isVideoCoverHidden = isFieldHidden(t('VIDEO_COVER'), 4);
  const isLogoHidden = isFieldHidden(t('LOGO'), 5);
  const isEstListHidden = isFieldHidden(t('EST_LIST'), 6);
  const isApplicationsListHidden = isFieldHidden(t('APPLICATIONS_LIST'), 7);
  const isCandidatsListHidden = isFieldHidden(t('CANDIDATS_LIST'), 8);
  const isSearchCriteriaCandidatsHidden = isFieldHidden(
    `${t('SEARCH_CRITERA_CANDIDATS')} : ${t('RAYON_MIN_MAX')}`,
    9,
  );
  const isSearchCriteriaTagsHidden = isFieldHidden(
    `${t('SEARCH_CRITERA_CANDIDATS')} : ${t('TAGS')}`,
    10,
  );
  const isSearchCriteriaExperienceHidden = isFieldHidden(
    `${t('SEARCH_CRITERA_CANDIDATS')} : ${t('YEAR_EXPERIENCE')}`,
    11,
  );
  const isCandidatDetailHidden = isFieldHidden(t('CANDIDAT_DETAIL'), 12);
  const isJobDetailHidden = isFieldHidden(t('JOB_DETAIL'), 13);
  const isEstList2Hidden = isFieldHidden(t('EST_LIST'), 14);
  const isUsersListHidden = isFieldHidden(t('USERS_LIST'), 15);
  const isEstDetailNameHidden = isFieldHidden(
    `${t('EST_DETAIL')} : ${t('NAME')}`,
    16,
  );
  const isSiretNumberHidden = isFieldHidden(t('SIRET_NUMBER'), 17);
  const isEstDetailUsersListHidden = isFieldHidden(
    `${t('EST_DETAIL')} : ${t('USERS_LIST')}`,
    18,
  );
  const isJobQualificationMaxHidden = isFieldHidden(
    t('JOB_QUALIFICATION_MAX'),
    19,
  );
  const isJobVideoSizeMaxHidden = isFieldHidden(t('JOB_VIDEO_SIZE_MAX'), 20);
  const isJobThumbnailSizeMaxHidden = isFieldHidden(
    t('JOB_THUMBNAIL_SIZE_MAX'),
    21,
  );
  const isJobTagsMaxHidden = isFieldHidden(t('JOB_TAGS_MAX'), 22);
  const isJobNameMaxHidden = isFieldHidden(t('JOB_NAME_MAX'), 23);
  const isJobReferenceNumberMaxHidden = isFieldHidden(
    t('JOB_REFERENCE_NUMBER_MAX'),
    24,
  );
  const isJobSearchCaracterMaxHidden = isFieldHidden(
    t('JOB_SEARCH_CHARACTER_MAX'),
    25,
  );

  const allHidden =
    isNameEnterpriseHidden &&
    isSirenNumberHidden &&
    isEntEstVideoHidden &&
    isVideoCoverHidden &&
    isLogoHidden &&
    isEstListHidden &&
    isApplicationsListHidden &&
    isCandidatsListHidden &&
    isSearchCriteriaCandidatsHidden &&
    isSearchCriteriaTagsHidden &&
    isSearchCriteriaExperienceHidden &&
    isCandidatDetailHidden &&
    isJobDetailHidden &&
    isEstList2Hidden &&
    isUsersListHidden &&
    isEstDetailNameHidden &&
    isSiretNumberHidden &&
    isEstDetailUsersListHidden &&
    isJobQualificationMaxHidden &&
    isJobVideoSizeMaxHidden &&
    isJobThumbnailSizeMaxHidden &&
    isJobTagsMaxHidden &&
    isJobNameMaxHidden &&
    isJobReferenceNumberMaxHidden;
  return (
    <>
      {(isMutateSettingsLoading || isSettingsFetching) ?? <Loading />}
      <Box mb={3}>
        <JTextFieldBase
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          attr={{ whiteBg: true }}
          label={t('SEARCH_SETTING')}
        />
      </Box>

      <Jpaper mb={3}>
        {allHidden && t('NOT_SETTINGS_FOUND')}
        <NumberSetting
          hidden={isNameEnterpriseHidden}
          label={t('NAME_ENTERPRISE')}
          rank={1}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entNameMaxChars,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entNameMaxChars)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entNameMaxChars,
                String(value),
              );
            }}
            label={t('MAX_CHARS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isSirenNumberHidden}
          label={t('SIREN_NUMBER')}
          rank={2}
        >
          <JNumberField
            disabled
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entSirenDigitsMin,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entSirenDigitsMin)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entSirenDigitsMin,
                String(value),
              );
            }}
            label={t('SIREN_MAX')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isEntEstVideoHidden}
          label={t('ENT_EST_VIDEO')}
          rank={3}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entVideoSizeMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entVideoSizeMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entVideoSizeMax,
                String(value),
              );
            }}
            label={t('SIZE_MO')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isVideoCoverHidden}
          label={t('VIDEO_COVER')}
          rank={4}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entThumbnailSizeMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entThumbnailSizeMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entThumbnailSizeMax,
                String(value),
              );
            }}
            label={t('SIZE_MO')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting hidden={isLogoHidden} label={t('LOGO')} rank={5}>
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entLogoSizeMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entLogoSizeMax)}
            onNumberChange={(value) => {
              onChangeSetting(EnterpriseCodeEnum.entLogoSizeMax, String(value));
            }}
            label={t('SIZE_MO')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting hidden={isEstListHidden} label={t('EST_LIST')} rank={6}>
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListEstSearchCharsMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entListEstSearchCharsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListEstSearchCharsMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS_SEARCH')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isApplicationsListHidden}
          label={t('APPLICATIONS_LIST')}
          rank={7}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListApplicationsSearchCharsMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entListApplicationsSearchCharsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListApplicationsSearchCharsMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS_SEARCH')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isCandidatsListHidden}
          label={t('CANDIDATS_LIST')}
          rank={8}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandidatesElementsMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entListCandidatesElementsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandidatesElementsMax,
                String(value),
              );
            }}
            label={t('MAX_BY_SECTOR')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isSearchCriteriaCandidatsHidden}
          label={`${t('SEARCH_CRITERA_CANDIDATS')} : ${t('RAYON_MIN_MAX')}`}
          rank={9}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandSearchAreaRange,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entListCandSearchAreaRange,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandSearchAreaRange,
                String(value),
              );
            }}
            label={t('RAYON_INC')}
            reset={isSettingsFetching}
          />
          <JNumberField
            error={entListCandSearchAreaRangeMinValidation}
            helperText={
              entListCandSearchAreaRangeMinValidation && 'Error de validation'
            }
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandSearchAreaRangeMin,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandSearchAreaRangeMin,
                String(value),
              );
            }}
            label={t('RAYON_MIN')}
            reset={isSettingsFetching}
          />
          <JNumberField
            error={entListCandSearchAreaRangeMaxValidation}
            helperText={
              entListCandSearchAreaRangeMaxValidation && 'Error de validation'
            }
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandSearchAreaRangeMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entListCandSearchAreaRangeMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandSearchAreaRangeMax,
                String(value),
              );
            }}
            label={t('RAYON_max')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isSearchCriteriaTagsHidden}
          label={`${t('SEARCH_CRITERA_CANDIDATS')} : ${t('TAGS')}`}
          rank={10}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandSearchTagsMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entListCandSearchTagsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandSearchTagsMax,
                String(value),
              );
            }}
            label={t('MAX_WORDS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isSearchCriteriaExperienceHidden}
          label={`${t('SEARCH_CRITERA_CANDIDATS')} : ${t('YEAR_EXPERIENCE')}`}
          rank={11}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandExpRange,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entListCandExpRange)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandExpRange,
                String(value),
              );
            }}
            label={t('YEARS_INC')}
            reset={isSettingsFetching}
          />
          <JNumberField
            error={entListCandExpMinValidation}
            helperText={entListCandExpMinValidation && 'Error de validation'}
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandExpMin,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entListCandExpMin)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandExpMin,
                String(value),
              );
            }}
            label={t('YEARS_MIN')}
            reset={isSettingsFetching}
          />

          <JNumberField
            error={entListCandExpMaxValidation}
            helperText={entListCandExpMaxValidation && 'Error de validation'}
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListCandExpMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entListCandExpMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListCandExpMax,
                String(value),
              );
            }}
            label={t('YEARS_MAX')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isCandidatDetailHidden}
          label={t('CANDIDAT_DETAIL')}
          rank={12}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entCandDetailsSimilarProfilesMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entCandDetailsSimilarProfilesMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entCandDetailsSimilarProfilesMax,
                String(value),
              );
            }}
            label={t('PROFILES_MAX')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entCandDetailsSimilarProfilesExpRange,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entCandDetailsSimilarProfilesExpRange,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entCandDetailsSimilarProfilesExpRange,
                String(value),
              );
            }}
            label={t('YEAR_EXPERIENCE')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isJobDetailHidden}
          label={t('JOB_DETAIL')}
          rank={13}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobOfferDetailsAppElements,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entJobOfferDetailsAppElements,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entJobOfferDetailsAppElements,
                String(value),
              );
            }}
            label={t('CANDS_RELATIVE')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isEstList2Hidden}
          label={t('EST_LIST')}
          rank={14}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entListEstElements,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entListEstElements)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entListEstElements,
                String(value),
              );
            }}
            label={t('ELMENT_PER_PAGE')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entEstListUsersSearchCharsMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entEstListUsersSearchCharsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entEstListUsersSearchCharsMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS_SEARCH')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isUsersListHidden}
          label={t('USERS_LIST')}
          rank={15}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entEstListUsersMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entEstListUsersMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entEstListUsersMax,
                String(value),
              );
            }}
            label={t('ELMENT_PER_PAGE')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entEstListUsersSearchCharsMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entEstListUsersSearchCharsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entEstListUsersSearchCharsMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS_SEARCH')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isEstDetailNameHidden}
          label={`${t('EST_DETAIL')} : ${t('NAME')}`}
          rank={16}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entEstNameCharsMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entEstNameCharsMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entEstNameCharsMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isSiretNumberHidden}
          label={`${t('EST_DETAIL')} : ${t('SIRET')}`}
          rank={17}
        >
          <JNumberField
            disabled
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entEstSiretDigitsMin,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entEstSiretDigitsMin)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entEstSiretDigitsMin,
                String(value),
              );
            }}
            label={t('SIRET_MAX')}
            reset={isSettingsFetching}
          />
        </NumberSetting>

        <NumberSetting
          hidden={isEstDetailUsersListHidden}
          label={`${t('EST_DETAIL')} : ${t('USERS_LIST')}`}
          rank={18}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entEstListUserElements,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entEstListUserElements)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entEstListUserElements,
                String(value),
              );
            }}
            label={t('ELMENT_PER_PAGE')}
            reset={isSettingsFetching}
          />
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entEstListUserSearchCharsMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entEstListUserSearchCharsMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entEstListUserSearchCharsMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS_SEARCH')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={isJobQualificationMaxHidden}
          label={t('JOB_QUALIFICATION_MAX')}
          rank={19}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobQualificationMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entJobQualificationMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entJobQualificationMax,
                String(value),
              );
            }}
            label={t('MAX_QUAL')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={isJobVideoSizeMaxHidden}
          label={t('JOB_VIDEO_SIZE_MAX')}
          rank={20}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobVideoSizeMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entJobVideoSizeMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entJobVideoSizeMax,
                String(value),
              );
            }}
            label={t('SIZE_MO')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={isJobThumbnailSizeMaxHidden}
          label={t('JOB_THUMBNAIL_SIZE_MAX')}
          rank={21}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobThumbnailSizeMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entJobThumbnailSizeMax)}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entJobThumbnailSizeMax,
                String(value),
              );
            }}
            label={t('SIZE_MO')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={isJobTagsMaxHidden}
          label={t('JOB_TAGS_MAX')}
          rank={22}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobTagsMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entJobTagsMax)}
            onNumberChange={(value) => {
              onChangeSetting(EnterpriseCodeEnum.entJobTagsMax, String(value));
            }}
            label={t('MAX_TAGS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={isJobNameMaxHidden}
          label={t('JOB_NAME_MAX')}
          rank={23}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobNameMax,
            )}
            newValue={getValueByCode(EnterpriseCodeEnum.entJobNameMax)}
            onNumberChange={(value) => {
              onChangeSetting(EnterpriseCodeEnum.entJobNameMax, String(value));
            }}
            label={t('MAX_CHARS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={isJobReferenceNumberMaxHidden}
          label={t('JOB_REFERENCE_NUMBER_MAX')}
          rank={24}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobReferenceNumberMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entJobReferenceNumberMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entJobReferenceNumberMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
        <NumberSetting
          hidden={isJobSearchCaracterMaxHidden}
          label={t('JOB_SEARCH_CHARACTER_MAX')}
          rank={25}
        >
          <JNumberField
            size="medium"
            name="setting-field"
            defaultValue={getDefaultValueByCode(
              EnterpriseCodeEnum.entJobSearchCharacterMax,
            )}
            newValue={getValueByCode(
              EnterpriseCodeEnum.entJobSearchCharacterMax,
            )}
            onNumberChange={(value) => {
              onChangeSetting(
                EnterpriseCodeEnum.entJobSearchCharacterMax,
                String(value),
              );
            }}
            label={t('MAX_CHARS_SEARCH')}
            reset={isSettingsFetching}
          />
        </NumberSetting>
      </Jpaper>
    </>
  );
};

export default EnterprisesSettings;
